import { ReactElement, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  InputBase,
  InputAdornment,
  CircularProgress,
  Button,
  Avatar,
  SxProps,
  Theme,
} from "@mui/material";
import { Add, Check, KeyboardArrowDown } from "@mui/icons-material";
import { CiSearch } from "react-icons/ci";

import DialogMenu from "../dialog-menu";
import { setSelectedClient, useGetAllClientsQuery } from "@/services";
import { useAppDispatch, useAppSelector } from "@/store";
import { useDebounce } from "@/hooks";

interface ClientPickerProps {
  tenantId: string;
  sx?: SxProps<Theme>;
}

const ClientPicker: React.FC<ClientPickerProps> = ({ tenantId, sx }): ReactElement => {
  const dispatch = useAppDispatch();
  const { clients, selectedClient } = useAppSelector((state) => state.client);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  function openClientPickerMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  const closeClientPickerMenu = (): void => {
    setAnchorEl(null);
    setSearch("");
  };

  const filterClients = useDebounce(setSearch, 1200);

  const { isLoading, isFetching } = useGetAllClientsQuery(
    { searchKeyword: search, pageNo: 1, pageSize: 25, tenantId:tenantId },
    {
      refetchOnMountOrArgChange: true,
      skip: !open || !tenantId,
    }
  );

  return (
    <>
      <Button
        id="client-picker-anchor"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-controls={open ? "client-picker-menu" : undefined}
        sx={{
          width: { sm: 267 },
          justifyContent: "flex-start",
          padding: {
            xs: 0.5,
            sm: "10px 12px",
          },
          borderRadius: "4px",
          textAlign: "left",
          whiteSpace: "nowrap",
          minWidth: "initial",
          border: { xs: "1px solid tranparent", sm: 0 },
          "&[aria-expanded='true']": {
            zIndex: 1500,
            border: {
              xs: "1px solid #0B494B",
              sm: 0,
            },
            boxShadow: {
              xs: "0px 0px 0px 4px #0B494B3D",
              sm: "0px 1px 2px 0px rgba(78, 78, 78, 0.05), 0px 0px 0px 4px rgba(11, 73, 75, 0.24)",
            },
          },
          ...sx,
        }}
        onClick={openClientPickerMenu}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: "100%",
            gap: "10px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          {isLoading ? (
            <CircularProgress size={18} />
          ) : (
            <>
              <Box
                sx={{
                  width: {
                    xs: 30,
                    sm: 34,
                  },
                  height: {
                    xs: 30,
                    sm: 34,
                  },
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#CDD3D3",
                }}
              >
                {selectedClient ? (
                  <Avatar
                    src={selectedClient.profileImage || undefined}
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                ) : (
                  <Add
                    sx={{
                      color: "#0b490b",
                      width: {
                        xs: 18,
                        sm: 24,
                      },
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  display: {
                    xs: "none",
                    sm: "initial",
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  sx={{
                    color: selectedClient ? "#8E9494" : "#030712",
                    height: selectedClient ? "14px" : "auto",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: selectedClient ? 12 : 14,
                      fontWeight: selectedClient ? 400 : 500,
                    }}
                  >
                    Select client
                  </Typography>
                  <KeyboardArrowDown sx={{ color: "#8E9494", fontSize: 14 }} />
                </Stack>

                {selectedClient ? (
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#030712",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "pre",
                    }}
                  >
                    {selectedClient.firstName}{" "}
                    <Typography
                      component="span"
                      sx={{ fontWeight: 400, color: "#8E9494" }}
                    >
                      ({selectedClient.email})
                    </Typography>
                  </Typography>
                ) : null}
              </Box>
            </>
          )}
        </Stack>
      </Button>

      <DialogMenu
        sx={{
          "& .MuiPaper-root": {
            width: "268px",
            borderRadius: "4px",
            border: 0,
          },
        }}
        id="client-picker-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeClientPickerMenu}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: "10px" }}>
          <InputBase
            fullWidth
            sx={{
              backgroundColor: "#F1F2F3",
              border: "1px solid #EFEFEF",
              padding: "12px 1rem",
              borderRadius: "8px",
              mb: "5px",
              fontSize: "14px",
              fontFamily: "Plus Jakarta Sans",
              fontWeight: 500,
              lineHeight: "20px",
              "& .MuiInputBase-input": {
                height: "initial",
              },
            }}
            placeholder="Search"
            startAdornment={
              <InputAdornment position="start">
                <CiSearch size={18} />
              </InputAdornment>
            }
            endAdornment={
              isFetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null
            }
            onChange={(e) => filterClients(e.target.value)}
          />

          {!isFetching && clients.length === 0 ? (
            <Box py="5px" px="10px">
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Plus Jakarta Sans",
                }}
              >
                No Clients Found
              </Typography>
            </Box>
          ) : (
            <Box
              className="client-list"
              sx={{
                maxHeight: 300,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: 3,
                },
                "&::-webkit-scrollbar-thumb": {
                  width: 3,
                  background: "#D9D9D9",
                  borderRadius: "1px",
                },
              }}
            >
              {clients.map((client) => (
                <Stack
                  key={client._id}
                  direction="row"
                  sx={{
                    padding: "5px 10px",
                    cursor: "pointer",
                    gap: 2,
                    alignItems: "center",
                  }}
                  onClick={() => {
                    dispatch(setSelectedClient(client));
                    closeClientPickerMenu();
                  }}
                >
                  <Avatar
                    src={client.profileImage}
                    sx={{
                      width: 34,
                      height: 34,
                    }}
                  />
                  <Box>
                    <Typography
                      color="rgb(55, 65, 81)"
                      fontWeight={500}
                      fontSize={14}
                    >
                      {client.firstName}
                    </Typography>
                    <Typography color="#8E9494" fontSize={12}>
                      {client.email}
                    </Typography>
                  </Box>

                  {client._id === selectedClient?._id ? (
                    <IconButton
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: "#0B494B",
                        marginLeft: "auto",
                      }}
                    >
                      <Check sx={{ width: 12, height: 12, color: "#fff" }} />
                    </IconButton>
                  ) : null}
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      </DialogMenu>
    </>
  );
};

export default ClientPicker;
